import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Autocomplete } from '@mui/material';
import TextField from 'components/text-field';
import { GQLCreatePartyAssociation } from 'graphql/parties/mutations';
import React, { FC } from 'react';
import { Controller } from 'react-hook-form';

import { KindField } from './components/kindField';
import { useModalCreateAssociationHooks } from './hooks';
import { useStyles } from './styles';
import { autocompleteRootProps } from 'v2-utils/autocomplete';

type ModalCreateAssociationProps = {
  partyId: string,
  isTitleCompany: boolean,
  isTitleAgency: boolean,
  open: boolean,
  onSuccess: (data: GQLCreatePartyAssociation) => void,
  onClose: () => void,
  onError: (error: unknown) => void,
};

export const ModalCreateAssociation: FC<ModalCreateAssociationProps> = ({
  partyId,
  isTitleCompany,
  isTitleAgency,
  open,
  onSuccess,
  onClose,
  onError
}) => {
  const classes = useStyles();
  const {
    reset,
    control,
    createAssociation,
    parties,
    setSearchText,
    setValue,
    errors,
    t,
    isValid,
    allowedAssociations
  } = useModalCreateAssociationHooks({ partyId, onSuccess, onClose, onError });

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleOnClose} fullWidth maxWidth="md">
      <DialogTitle className={classes.title}>
        {t('parties:create-assoc')}
      </DialogTitle>

      <DialogContent>
        <form onSubmit={(e) => {
          e.preventDefault();
          createAssociation();
        }}
        >
          <Grid container direction="row" spacing={2}>
            <Grid item xs={8}>
              <Controller
                name="selected"
                control={control}
                render={({ field: { ref, onChange, ...field } }) => (
                  <Autocomplete
                    {...field}
                    freeSolo
                    fullWidth
                    ref={ref}
                    options={parties}
                    filterOptions={(options) => options}
                    getOptionLabel={(party) => (typeof party === "string" ? party : party.name)}
                    onInputChange={(_, value, reason) => {
                      if (reason === 'input') {
                        setSearchText(value);
                      }
                    }}
                    onChange={(_, value) => {
                      if (value && typeof value === 'object') {
                        onChange(value);
                        setValue('_id', value._id);
                        setValue('name', value.name);
                        setValue('kind', '');
                      } else {
                        onChange(null);
                        setValue('_id', '');
                        setValue('name', '');
                        setValue('kind', '');
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputRef={params.InputProps.ref}
                        InputProps={autocompleteRootProps(params.inputProps)}
                        label={t('users:name')}
                        error={!!errors._id}
                        helperText={
                          errors._id?.message || t('validations:required')
                        }
                      />
                    )}
                  />
                )}
              />
            </Grid>

            <Grid item xs={4}>
              <KindField
                isTitleCompany={isTitleCompany}
                isTitleAgency={isTitleAgency}
                control={control}
                errors={errors}
                listingEntries={allowedAssociations}
              />
            </Grid>

            <Grid item xs={12}>
              <Grid container direction="row" justifyContent="flex-end">
                <Grid item className={classes.buttonContainer}>
                  <Button
                    type="submit"
                    disableElevation
                    disabled={!isValid}
                    variant="contained"
                    color="secondary"
                    className={classes.buttonText}
                  >
                    {t('users:create')}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};
